import type { StoreApi } from "zustand";
import { shallow } from "zustand/shallow";

// This is an internal type from zustand that's maybe importable
// with the right type unwrapping
type StoreSubscribeWithSelector<T> = StoreApi<T> & {
  subscribe: {
    (listener: (selectedState: T, previousSelectedState: T) => void): () => void;
    <U>(
      selector: (state: T) => U,
      listener: (selectedState: U, previousSelectedState: U) => void,
      options?: {
        equalityFn?: (a: U, b: U) => boolean;
        fireImmediately?: boolean;
      },
    ): () => void;
  };
};

type ValueOf<T> = T[keyof T];

export type DepSelector<S, R = unknown> = (s: S) => R;

// S will be the store type, and R will be the type of the value returned by the selector
export type ConstrainedDepSelector<S, R extends ValueOf<S> = ValueOf<S>> = (s: S) => R;

export interface NodeDef<S, K extends keyof S = keyof S> {
  key: K;
  deps: ConstrainedDepSelector<S>[];
  // compute takes some slice of the store and returns a value that is put back into the store
  compute: (s: S) => S[K];
  // optional custom setter if you don't want {[key]: value}
  set?: (api: StoreSubscribeWithSelector<S>, value: S[K]) => void;
}

// For each of our nodes, subscribe to the store updates
export function wireNodes<S>(api: StoreSubscribeWithSelector<S>, nodeDefinitions: NodeDef<S>[]) {
  nodeDefinitions.forEach((definition) => {
    api.subscribe(
      (state) => definition.deps.map((selector) => selector(state)),
      () => {
        const value = definition.compute(api.getState());
        if (definition.set) definition.set(api, value);
        else api.setState({ [definition.key]: value } as Partial<S>);
      },
      { equalityFn: shallow },
    );
  });
}
